import React, { createContext, useState, useEffect, useContext } from 'react';
import { db } from './firebase';
import { 
  doc, 
  getDoc, 
  setDoc, 
  onSnapshot, 
  Timestamp 
} from 'firebase/firestore';

// Create a context for profile data
const ProfileContext = createContext();

// Provider component to wrap around the app
export const ProfileProvider = ({ children }) => {
  const [profileData, setProfileData] = useState({
    profilePicture: null,
    rentalName: 'Bike Rental Service',
    rentalAddress: '',
    phoneNumbers: [''],
    emails: [''],
    websites: ['']
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Firebase document reference
  const profileDocRef = doc(db, 'profiles', 'rental_profile');

  // Load profile data from Firebase on component mount
  useEffect(() => {
    let unsubscribe = null;

    const initializeProfile = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // First, try to load from Firebase
        const profileDoc = await getDoc(profileDocRef);
        
        if (profileDoc.exists()) {
          const firebaseProfileData = profileDoc.data();
          
          // Ensure all required fields exist with proper defaults
          const normalizedData = {
            profilePicture: firebaseProfileData.profilePicture || null,
            rentalName: firebaseProfileData.rentalName || 'Bike Rental Service',
            rentalAddress: firebaseProfileData.rentalAddress || '',
            phoneNumbers: Array.isArray(firebaseProfileData.phoneNumbers) && firebaseProfileData.phoneNumbers.length > 0 
              ? firebaseProfileData.phoneNumbers 
              : [''],
            emails: Array.isArray(firebaseProfileData.emails) && firebaseProfileData.emails.length > 0 
              ? firebaseProfileData.emails 
              : [''],
            websites: Array.isArray(firebaseProfileData.websites) && firebaseProfileData.websites.length > 0 
              ? firebaseProfileData.websites 
              : ['']
          };

          setProfileData(normalizedData);
          setIsConnected(true);
          console.log('Profile data loaded from Firebase:', normalizedData);
        } else {
          // No Firebase document exists, check localStorage for migration
          const localProfileData = await migrateFromLocalStorage();
          if (localProfileData) {
            setProfileData(localProfileData);
            // Save migrated data to Firebase
            await saveToFirebase(localProfileData);
          }
          setIsConnected(true);
        }

        // Set up real-time listener for profile updates
        unsubscribe = onSnapshot(profileDocRef, (doc) => {
          if (doc.exists()) {
            const updatedData = doc.data();
            
            // Normalize the data before setting
            const normalizedData = {
              profilePicture: updatedData.profilePicture || null,
              rentalName: updatedData.rentalName || 'Bike Rental Service',
              rentalAddress: updatedData.rentalAddress || '',
              phoneNumbers: Array.isArray(updatedData.phoneNumbers) && updatedData.phoneNumbers.length > 0 
                ? updatedData.phoneNumbers 
                : [''],
              emails: Array.isArray(updatedData.emails) && updatedData.emails.length > 0 
                ? updatedData.emails 
                : [''],
              websites: Array.isArray(updatedData.websites) && updatedData.websites.length > 0 
                ? updatedData.websites 
                : ['']
            };

            setProfileData(normalizedData);
            console.log('Profile data updated from Firebase real-time listener');
          }
        }, (error) => {
          console.error('Firebase listener error:', error);
          setError('Real-time updates unavailable');
        });

      } catch (error) {
        console.error('Error initializing profile from Firebase:', error);
        setError('Failed to connect to Firebase');
        setIsConnected(false);
        
        // Fallback to localStorage
        const localData = await migrateFromLocalStorage();
        if (localData) {
          setProfileData(localData);
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeProfile();

    // Cleanup listener on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Migrate data from localStorage (for backward compatibility)
  const migrateFromLocalStorage = async () => {
    try {
      const savedProfileData = localStorage.getItem('profileData');
      if (savedProfileData) {
        const localData = JSON.parse(savedProfileData);
        console.log('Found profile data in localStorage, migrating to Firebase:', localData);
        
        // Ensure data structure is valid
        const migratedData = {
          profilePicture: localData.profilePicture || null,
          rentalName: localData.rentalName || 'Bike Rental Service',
          rentalAddress: localData.rentalAddress || '',
          phoneNumbers: Array.isArray(localData.phoneNumbers) && localData.phoneNumbers.length > 0 
            ? localData.phoneNumbers 
            : [''],
          emails: Array.isArray(localData.emails) && localData.emails.length > 0 
            ? localData.emails 
            : [''],
          websites: Array.isArray(localData.websites) && localData.websites.length > 0 
            ? localData.websites 
            : ['']
        };

        return migratedData;
      }
    } catch (error) {
      console.error('Error migrating from localStorage:', error);
    }
    return null;
  };

  // Save profile data to Firebase
  const saveToFirebase = async (data) => {
    try {
      const profileWithMetadata = {
        ...data,
        lastUpdated: Timestamp.now(),
        version: '1.0'
      };

      await setDoc(profileDocRef, profileWithMetadata);
      console.log('Profile data saved to Firebase successfully');
      return true;
    } catch (error) {
      console.error('Error saving profile to Firebase:', error);
      throw error;
    }
  };

  // Save to localStorage as backup
  const saveToLocalStorage = (data) => {
    try {
      localStorage.setItem('profileData', JSON.stringify(data));
      console.log('Profile data backed up to localStorage');
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  // Update profile data function
  const updateProfileData = async (newData) => {
    try {
      setIsSaving(true);
      setError(null);

      // Validate data structure
      const validatedData = {
        profilePicture: newData.profilePicture || null,
        rentalName: newData.rentalName || 'Bike Rental Service',
        rentalAddress: newData.rentalAddress || '',
        phoneNumbers: Array.isArray(newData.phoneNumbers) && newData.phoneNumbers.length > 0 
          ? newData.phoneNumbers.filter(phone => phone.trim() !== '') 
          : [''],
        emails: Array.isArray(newData.emails) && newData.emails.length > 0 
          ? newData.emails.filter(email => email.trim() !== '') 
          : [''],
        websites: Array.isArray(newData.websites) && newData.websites.length > 0 
          ? newData.websites.filter(website => website.trim() !== '') 
          : ['']
      };

      // Update local state immediately for responsiveness
      setProfileData(validatedData);

      // Try to save to Firebase
      if (isConnected) {
        await saveToFirebase(validatedData);
      } else {
        throw new Error('Firebase not connected');
      }

      // Backup to localStorage
      saveToLocalStorage(validatedData);

      // Dispatch custom event for components that might need it
      window.dispatchEvent(new CustomEvent('profileUpdated', { 
        detail: validatedData 
      }));

      console.log('Profile updated successfully');
      
    } catch (error) {
      console.error('Error updating profile:', error);
      
      // If Firebase save fails, still save locally
      saveToLocalStorage(newData);
      setError('Profile saved locally. Firebase sync failed.');
      
      // Dispatch event even if Firebase fails
      window.dispatchEvent(new CustomEvent('profileUpdated', { 
        detail: newData 
      }));
    } finally {
      setIsSaving(false);
    }
  };

  // Retry Firebase connection
  const retryFirebaseConnection = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const profileDoc = await getDoc(profileDocRef);
      setIsConnected(true);
      
      if (profileDoc.exists()) {
        const firebaseData = profileDoc.data();
        setProfileData({
          profilePicture: firebaseData.profilePicture || null,
          rentalName: firebaseData.rentalName || 'Bike Rental Service',
          rentalAddress: firebaseData.rentalAddress || '',
          phoneNumbers: Array.isArray(firebaseData.phoneNumbers) && firebaseData.phoneNumbers.length > 0 
            ? firebaseData.phoneNumbers 
            : [''],
          emails: Array.isArray(firebaseData.emails) && firebaseData.emails.length > 0 
            ? firebaseData.emails 
            : [''],
          websites: Array.isArray(firebaseData.websites) && firebaseData.websites.length > 0 
            ? firebaseData.websites 
            : ['']
        });
      }
      
      console.log('Firebase connection restored');
    } catch (error) {
      console.error('Failed to retry Firebase connection:', error);
      setError('Firebase connection failed');
      setIsConnected(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Get profile data (useful for external components)
  const getProfileData = () => {
    return profileData;
  };

  // Check if profile is complete
  const isProfileComplete = () => {
    return !!(
      profileData.rentalName &&
      profileData.rentalName !== 'Bike Rental Service' &&
      profileData.rentalAddress &&
      profileData.phoneNumbers.some(phone => phone.trim() !== '') &&
      profileData.emails.some(email => email.trim() !== '')
    );
  };

  const contextValue = {
    profileData,
    updateProfileData,
    getProfileData,
    isProfileComplete,
    retryFirebaseConnection,
    // Firebase status
    isLoading,
    isConnected,
    error,
    isSaving,
    // Utility functions
    clearError: () => setError(null)
  };

  return (
    <ProfileContext.Provider value={contextValue}>
      {children}
    </ProfileContext.Provider>
  );
};

// Custom hook to use the profile context
export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};

// Hook for components that only need to read profile data
export const useProfileData = () => {
  const { profileData, isLoading } = useProfile();
  return { profileData, isLoading };
};

// Hook for components that need Firebase status
export const useProfileStatus = () => {
  const { isLoading, isConnected, error, isSaving } = useProfile();
  return { isLoading, isConnected, error, isSaving };
};

export default ProfileProvider;